import { useMemo, useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  RefreshCw,
  ImageIcon,
  Mail,
  Shield,
  ChevronLeft,
  Phone,
  History,
  Plus,
  FileText,
  Paperclip,
  Loader2,
  PenTool,
  Camera,
} from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import {
  Button, LoadingScreen, Skeleton,
  DetailSection, PropertyRow, StatusBadge
} from '../components/ui';
import { ImageThumbnailGrid } from '../components/ui/ImageThumbnailGrid';
import { TrackingLinkGenerator } from '../components/bookings/TrackingLinkGenerator';
import { CancellationModal } from '../components/booking/CancellationModal';
import { RescheduleFlow } from '../components/booking/RescheduleFlow';
import { BookingActions } from '../components/booking/BookingActions';
import { WorkflowChecklistSection } from '../components/booking/WorkflowChecklistSection';
import { CustomerVehicleSummary } from '../components/cases/CustomerVehicleSummary';
import { JobTimeline } from '../components/jobs/JobTimeline';
import { useAvailableTransitions } from '../hooks/useAvailableTransitions';
import {
  useBooking,
  useBookingTimeline,
  useBookingImages,
  useDeleteBookingImage,
  useResendConfirmation,
  useResendBookingEmail,
  useBookingAttachments,
} from '../hooks/useBookings';
import { detailStyles, pageStyles } from '../lib/styles';
import { api } from '../lib/api';
import { getApiBase } from '../lib/apiBase';
import { compressImages } from '../lib/imageCompression';
import { signatureTemplatesApi } from '../services/signatureTemplatesApi';
import type { IntakeField, UnifiedCaseType } from '@leal-crm/shared';

/**
 * Map booking type to unified case type for workflow lookup
 */
function getBookingType(type?: string, bookingType?: string, caseCategory?: string): UnifiedCaseType {
  switch (bookingType) {
    case 'insurance':
      return 'insurance';
    case 'warranty':
      return 'warranty';
    case 'private':
      return 'private';
    default:
      break;
  }

  switch (caseCategory) {
    case 'seguro':
      return 'insurance';
    case 'garantia':
      return 'warranty';
    default:
      break;
  }

  switch (type) {
    case 'seguro':
      return 'insurance';
    default:
      return 'private';
  }
}

function getBookingTypeLabel(type?: string, bookingType?: string, caseCategory?: string): string {
  const normalized = getBookingType(type, bookingType, caseCategory);
  if (normalized === 'insurance') return 'Seguro';
  if (normalized === 'warranty') return 'Garantía';
  return 'Particular';
}

export function BookingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { success: showSuccess, error: showError } = useToast();
  const apiBase = getApiBase();

  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [rescheduleModalOpen, setRescheduleModalOpen] = useState(false);
  const [isSendingUploadLink, setIsSendingUploadLink] = useState(false);
  const [isSendingSignatureLink, setIsSendingSignatureLink] = useState(false);
  const [selectedSignatureTemplateId, setSelectedSignatureTemplateId] = useState('');

  const { data: booking, isLoading, isError, error, refetch } = useBooking(id);
  const { data: bookingTimeline = [], isLoading: isTimelineLoading, refetch: refetchTimeline } = useBookingTimeline(id);
  const { data: bookingImages, isLoading: isLoadingImages, refetch: refetchImages } = useBookingImages(id);
  const {
    data: workflowAttachments,
    isLoading: isLoadingWorkflowAttachments,
    refetch: refetchWorkflowAttachments,
  } = useBookingAttachments(booking?.id);
  const {
    data: availableTransitions = [],
    refetch: refetchTransitions,
  } = useAvailableTransitions(id);
  const { data: signatureTemplates = [] } = useQuery({
    queryKey: ['signature-templates-active'],
    queryFn: () => signatureTemplatesApi.list(false),
    staleTime: 60_000,
  });
  const deleteImageMutation = useDeleteBookingImage();
  const resendConfirmationMutation = useResendConfirmation();
  const resendNotificationMutation = useResendBookingEmail();

  const [requirementValues, setRequirementValues] = useState<Record<string, Record<string, unknown>>>({});
  const [dialogFieldValues, setDialogFieldValues] = useState<Record<string, Record<string, unknown>>>({});
  const [attachmentCounts, setAttachmentCounts] = useState<Record<string, number>>({});

  // Redirect to Case view (Case-First architecture)
  useEffect(() => {
    if (booking?.caseId && !isLoading) {
      navigate(`/cases/${booking.caseId}`, { replace: true });
    }
  }, [booking?.caseId, isLoading, navigate]);

  const isSignatureImage = (img: { r2Key?: string; originalFilename?: string }) =>
    img.r2Key?.includes('/signatures/') || img.originalFilename === 'signature.png';

  const bookingImagePhotos = useMemo(
    () => (bookingImages || []).filter((img) => img.contentType?.startsWith('image/') && !isSignatureImage(img)),
    [bookingImages]
  );
  const bookingImageDocuments = useMemo(
    () => (bookingImages || []).filter((img) => !img.contentType?.startsWith('image/') && !isSignatureImage(img)),
    [bookingImages]
  );
  const bookingSignatureImages = useMemo(
    () => (bookingImages || []).filter((img) => isSignatureImage(img)),
    [bookingImages]
  );
  const processDocuments = useMemo(
    () => (workflowAttachments || []).filter((att) => att.category === 'file'),
    [workflowAttachments]
  );
  const signatureDocuments = useMemo(
    () => (workflowAttachments || []).filter((att) => att.category === 'signature'),
    [workflowAttachments]
  );
  // Build enriched signature entries by matching images to timeline events
  const signatureEntries = useMemo(() => {
    const submitted = (bookingTimeline || []).filter(
      (e) => e.metadata?.type === 'signature_submitted'
    );
    const requests = (bookingTimeline || []).filter(
      (e) => e.metadata?.type === 'signature_request'
    );

    // Map each booking signature image to its closest submitted event (within 60s)
    const enrichedBookingSigs = bookingSignatureImages.map((img) => {
      const imgTs = typeof img.uploadedAt === 'number' ? img.uploadedAt : 0;
      let bestEvent: (typeof submitted)[0] | null = null;
      let bestDiff = Infinity;
      for (const ev of submitted) {
        const diff = Math.abs(ev.createdAt - imgTs);
        if (diff < bestDiff && diff < 120) { bestDiff = diff; bestEvent = ev; }
      }
      // Find the most recent request BEFORE this submission for template info
      let reqEvent: (typeof requests)[0] | null = null;
      if (bestEvent) {
        for (const r of requests) {
          if (r.createdAt <= bestEvent.createdAt && (!reqEvent || r.createdAt > reqEvent.createdAt)) {
            reqEvent = r;
          }
        }
      }
      const templateName = (reqEvent?.metadata?.templateName as string) || null;
      const message = (bestEvent?.metadata?.message as string) || (reqEvent?.metadata?.message as string) || null;
      return {
        type: 'booking_image' as const,
        id: img.id,
        url: `${apiBase}/api/bookings/${booking?.id}/images/${img.id}`,
        label: templateName || 'Firma del cliente',
        message,
        signedAt: imgTs > 0 ? new Date(imgTs * 1000) : null,
      };
    });

    // Workflow attachment signatures
    const enrichedWfSigs = signatureDocuments.map((att) => {
      const attTs = att.uploadedAt || 0;
      let reqEvent: (typeof requests)[0] | null = null;
      for (const r of requests) {
        if (r.createdAt <= attTs && (!reqEvent || r.createdAt > reqEvent.createdAt)) {
          reqEvent = r;
        }
      }
      const templateName = (reqEvent?.metadata?.templateName as string) || null;
      const message = (reqEvent?.metadata?.message as string) || null;
      return {
        type: 'workflow_attachment' as const,
        id: att.id,
        url: `${apiBase}${att.url}`,
        label: templateName || att.checklistItemLabel || 'Firma',
        message,
        signedAt: attTs > 0 ? new Date(attTs * 1000) : null,
        uploadedBy: att.uploadedBy?.name || null,
      };
    });

    return [...enrichedWfSigs, ...enrichedBookingSigs];
  }, [bookingSignatureImages, signatureDocuments, bookingTimeline, apiBase, booking?.id]);

  const signatureDeclines = useMemo(() => {
    const requests = (bookingTimeline || []).filter((e) => e.metadata?.type === 'signature_request');
    return (bookingTimeline || [])
      .filter((e) => e.metadata?.type === 'signature_declined')
      .map((event) => {
        let reqEvent: (typeof requests)[0] | null = null;
        for (const r of requests) {
          if (r.createdAt <= event.createdAt && (!reqEvent || r.createdAt > reqEvent.createdAt)) {
            reqEvent = r;
          }
        }
        return {
          id: event.id,
          createdAt: event.createdAt,
          templateName: (reqEvent?.metadata?.templateName as string) || 'Solicitud de firma',
          message: (event.metadata?.message as string) || (reqEvent?.metadata?.message as string) || null,
        };
      });
  }, [bookingTimeline]);

  const transitionGroups = useMemo(() => {
    const groups = new Map<string, {
      statusId: string;
      statusLabel: string;
      statusCode: string;
      colors?: { text: string; bg: string; border: string };
      requirements: typeof availableTransitions[number]['requirements'];
      customFields: IntakeField[];
    }>();

    availableTransitions.forEach((transition) => {
      const groupKey = transition.toStatus;
      const existing = groups.get(groupKey);
      const dialogConfig = transition.dialogConfig as { customFields?: IntakeField[] } | null;
      const customFields = dialogConfig?.customFields || [];

      if (!existing) {
        groups.set(groupKey, {
          statusId: transition.toStatusMeta?.id || transition.transitionId,
          statusLabel: transition.toStatusLabel,
          statusCode: transition.toStatus,
          colors: transition.toStatusColors,
          requirements: transition.requirements ? [...transition.requirements] : [],
          customFields: [...customFields],
        });
      } else {
        (transition.requirements || []).forEach((req) => {
          if (!existing.requirements.find((item) => item.config.itemKey === req.config.itemKey)) {
            existing.requirements.push(req);
          }
        });
        customFields.forEach((field) => {
          if (!existing.customFields.find((item) => item.name === field.name)) {
            existing.customFields.push(field);
          }
        });
      }
    });

    return Array.from(groups.values()).map((group) => ({
      ...group,
      requirements: group.requirements
        .slice()
        .sort((a, b) => a.config.displayOrder - b.config.displayOrder),
    }));
  }, [availableTransitions]);

  const handleDeleteImage = async (imageId: string) => {
    if (!booking) return;
    if (!confirm('¿Está seguro de que desea eliminar esta imagen?')) return;
    try {
      await deleteImageMutation.mutateAsync({ bookingId: booking.id, imageId });
      showSuccess('Imagen eliminada');
    } catch {
      showError('Error al eliminar la imagen');
    }
  };

  const handleCancelSuccess = () => showSuccess('Cita cancelada exitosamente');
  const handleRescheduleSuccess = () => showSuccess('Cita reprogramada exitosamente');

  const handleSendUploadLink = async () => {
    if (!booking?.id) {
      showError('Booking no disponible');
      return;
    }
    setIsSendingUploadLink(true);
    try {
      await api.post(`/api/bookings/${booking.id}/send-upload-link`, {});
      showSuccess('Enlace de subida enviado');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Error al enviar enlace de subida');
    } finally {
      setIsSendingUploadLink(false);
    }
  };

  const handleSendSignatureLink = async (opts?: { templateId?: string; message?: string }) => {
    if (!booking?.id) {
      showError('Booking no disponible');
      return;
    }
    setIsSendingSignatureLink(true);
    try {
      let effectiveJobId = booking.jobId || null;
      if (!effectiveJobId) {
        try {
          const job = await api.get<{ id: string }>(`/api/jobs/by-booking/${booking.id}`);
          effectiveJobId = job.id;
        } catch (err) {
          // If no job exists yet, fall back to booking-based endpoint.
          if (err instanceof Error && (err as { status?: number }).status === 404) {
            await api.post(`/api/bookings/${booking.id}/send-signature-link`, opts || {});
            showSuccess('Enlace de firma enviado');
            return;
          }
          throw err;
        }
      }

      if (!effectiveJobId) {
        // Final fallback: booking-based endpoint
        await api.post(`/api/bookings/${booking.id}/send-signature-link`, opts || {});
        showSuccess('Enlace de firma enviado');
        return;
      }

      await api.post(`/api/jobs/${effectiveJobId}/send-signature-link`, opts || {});
      showSuccess('Enlace de firma enviado');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Error al enviar enlace de firma');
    } finally {
      setIsSendingSignatureLink(false);
    }
  };

  const handleResendConfirmation = async () => {
    if (!booking) return;
    try {
      const result = await resendConfirmationMutation.mutateAsync(booking.id);
      showSuccess(`Email reenviado a ${result.email}`);
    } catch {
      showError('Error al reenviar el email');
      refetch();
    }
  };

  if (isLoading) return <LoadingScreen />;

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center py-8">
        <AlertCircle className="h-8 w-8 text-red-500 mb-3" />
        <p className="text-sm text-gray-900 mb-1">Error al cargar la cita</p>
        <p className="text-xs text-gray-500 mb-3">{error?.message}</p>
        <div className="flex gap-2">
          <Button variant="secondary" size="sm" onClick={() => refetch()}>
            <RefreshCw className="h-3 w-3 mr-1.5" /> Reintentar
          </Button>
          <Link to="/jobs"><Button variant="secondary" size="sm">Volver</Button></Link>
        </div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="text-center py-8">
        <p className="text-xs text-gray-500">Cita no encontrada</p>
        <Link to="/jobs" className="text-xs text-blue-600 hover:underline mt-2 block">Volver</Link>
      </div>
    );
  }

  const scheduledDate = new Date(booking.scheduledAt * 1000);

  const handleSaveRequirements = async (statusId: string) => {
    if (!booking) return;
    const values = requirementValues[statusId] || {};
    try {
      await api.post(`/api/bookings/${booking.id}/checklist-values`, {
        statusId,
        values: Object.entries(values).map(([itemKey, value]) => ({
          itemKey,
          value,
        })),
      });
      showSuccess('Requisitos guardados');
      await refetchTransitions();
    } catch {
      showError('No se pudieron guardar los requisitos');
    }
  };


  return (
    <div className={detailStyles.container}>
      {/* Compact Header */}
      <div className={pageStyles.header}>
        <div className={pageStyles.headerLeft}>
          <Link to="/bookings" className={pageStyles.backButton}>
            <ChevronLeft className="h-4 w-4" />
          </Link>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h1 className={pageStyles.title}>
                {booking.bookingReference || `#${booking.id.slice(0, 8)}`}
              </h1>
              <StatusBadge
                status={booking.status}
                bookingType={getBookingType(booking.type, booking.bookingType, booking.caseCategory)}
              />
              {booking.caseId && (
                <Link
                  to={`/cases/${booking.caseId}`}
                  className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-2 py-0.5 text-[11px] text-gray-700 hover:bg-gray-50"
                  title="Abrir caso"
                >
                  Ver caso
                </Link>
              )}
            </div>
            <p className={pageStyles.subtitle}>
              {getBookingTypeLabel(booking.type, booking.bookingType, booking.caseCategory)} · {new Date(booking.createdAt * 1000).toLocaleDateString('es-ES')}
            </p>
          </div>
        </div>
      </div>

      <div className={detailStyles.grid}>
        {/* Main Content */}
        <div className={detailStyles.main}>
          {/* Booking Details */}
          <DetailSection title="Cita">
            <PropertyRow label="Fecha">
              {scheduledDate.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
            </PropertyRow>
            <PropertyRow label="Hora">
              {scheduledDate.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
            </PropertyRow>
            <PropertyRow label="Tipo">
              {getBookingTypeLabel(booking.type, booking.bookingType, booking.caseCategory)}
            </PropertyRow>
            {booking.bookingReference && (
              <PropertyRow label="Referencia">
                <span className="font-mono text-blue-600">{booking.bookingReference}</span>
              </PropertyRow>
            )}
            {booking.notes && (
              <PropertyRow label="Notas">{booking.notes}</PropertyRow>
            )}
          </DetailSection>

          {/* Insurance Info */}
          {getBookingType(booking.type, booking.bookingType, booking.caseCategory) === 'insurance' && (
            <DetailSection title="Seguro" action={<Shield className="h-3.5 w-3.5 text-gray-400" />}>
              {booking.insuranceCompany ? (
                <>
                  <PropertyRow label="Aseguradora">{booking.insuranceCompany}</PropertyRow>
                  {booking.insuranceClaimNumber && (
                    <PropertyRow label="Nº Siniestro">
                      <span className="font-mono">{booking.insuranceClaimNumber}</span>
                    </PropertyRow>
                  )}
                  {booking.insurancePolicyNumber && (
                    <PropertyRow label="Nº Póliza">
                      <span className="font-mono">{booking.insurancePolicyNumber}</span>
                    </PropertyRow>
                  )}
                  {booking.insuranceAdjusterName && (
                    <>
                      <div className="px-3 py-1.5 bg-gray-50 border-t border-gray-100">
                        <span className="text-[10px] font-medium text-gray-500 uppercase">Perito</span>
                      </div>
                      <PropertyRow label="Nombre">{booking.insuranceAdjusterName}</PropertyRow>
                      {booking.insuranceAdjusterPhone && (
                        <PropertyRow label="Teléfono" icon={<Phone className="h-3 w-3" />}>
                          <a href={`tel:${booking.insuranceAdjusterPhone}`} className="text-blue-600 hover:underline">
                            {booking.insuranceAdjusterPhone}
                          </a>
                        </PropertyRow>
                      )}
                      {booking.insuranceAdjusterEmail && (
                        <PropertyRow label="Email" icon={<Mail className="h-3 w-3" />}>
                          <a href={`mailto:${booking.insuranceAdjusterEmail}`} className="text-blue-600 hover:underline truncate">
                            {booking.insuranceAdjusterEmail}
                          </a>
                        </PropertyRow>
                      )}
                    </>
                  )}
                </>
              ) : (
                <div className="px-3 py-4 text-center">
                  <p className="text-xs text-gray-500">Sin datos de seguro</p>
                </div>
              )}
            </DetailSection>
          )}

          <CustomerVehicleSummary
            customer={booking.customer ? {
              id: booking.customer.id,
              name: booking.customer.name,
              phone: booking.customer.phone,
              email: booking.customer.email,
            } : undefined}
            vehicle={booking.vehicle ? {
              id: booking.vehicle.id,
              registration: booking.vehicle.registration,
              make: booking.vehicle.make,
              model: booking.vehicle.model,
              year: booking.vehicle.year,
              color: booking.vehicle.color,
            } : undefined}
          />

          {/* Requirements */}
          {transitionGroups.length > 0 && (
            <DetailSection title="Requisitos">
              <div className="space-y-4">
                {transitionGroups.map((group) => (
                  <div key={group.statusCode} className="rounded-lg border border-gray-200 p-3 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span
                          className="px-2 py-0.5 rounded-full text-[11px] font-medium border"
                          style={{
                            color: group.colors?.text,
                            backgroundColor: group.colors?.bg,
                            borderColor: group.colors?.border,
                          }}
                        >
                          {group.statusLabel || group.statusCode}
                        </span>
                        <span className="text-xs text-gray-500">Requisitos para continuar</span>
                      </div>
                      {group.requirements.length > 0 && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleSaveRequirements(group.statusId)}
                        >
                          Guardar
                        </Button>
                      )}
                    </div>

                    {group.requirements.length > 0 && (
                      <div className="space-y-3">
                        {group.requirements.map((req) => {
                          const statusId = group.statusId;
                          const itemKey = req.config.itemKey;
                          const currentValue = requirementValues[statusId]?.[itemKey] ?? req.value;
                          const source = req.config.validationRules?.source as string | undefined;
                          const description = req.config.validationRules?.description as string | undefined;
                          const isAttachment = ['photo', 'signature', 'file'].includes(req.config.itemType);
                          const countValue = attachmentCounts[itemKey] ?? (req.value as { count?: number } | undefined)?.count ?? 0;
                          const minCount = Number(req.config.validationRules?.minCount ?? 1);
                          const isComplete = (() => {
                            if (!req.config.isRequired) return true;
                            if (isAttachment) {
                              return countValue >= minCount;
                            }
                            if (req.config.itemType === 'boolean') return currentValue === true;
                            return currentValue !== null && currentValue !== undefined && currentValue !== '';
                          })();

                          return (
                            <div key={itemKey} className="rounded-md border border-gray-100 p-3">
                              <div className="flex items-start justify-between gap-3">
                                <div>
                                  <p className="text-sm font-medium text-gray-900">
                                    {req.config.itemLabel}
                                    {req.config.isRequired && <span className="text-red-500 ml-1">*</span>}
                                  </p>
                                  {description && (
                                    <p className="text-xs text-gray-500 mt-1">{description}</p>
                                  )}
                                </div>
                                {!isAttachment && (
                                  <span className="text-[10px] px-2 py-0.5 rounded-full border bg-gray-50 text-gray-600">
                                    {isComplete ? 'Completo' : 'Pendiente'}
                                  </span>
                                )}
                              </div>

                              {req.config.itemType === 'boolean' && (
                                <div className="mt-2 flex items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={currentValue === true}
                                    onChange={(e) =>
                                      setRequirementValues((prev) => ({
                                        ...prev,
                                        [statusId]: {
                                          ...(prev[statusId] || {}),
                                          [itemKey]: e.target.checked,
                                        },
                                      }))
                                    }
                                  />
                                  <span className="text-sm text-gray-700">Confirmado</span>
                                </div>
                              )}

                              {req.config.itemType === 'number' && (
                                <input
                                  className="mt-2 w-full rounded border border-gray-200 px-2 py-1 text-sm"
                                  type="number"
                                  value={typeof currentValue === 'number' ? currentValue : ''}
                                  onChange={(e) =>
                                    setRequirementValues((prev) => ({
                                      ...prev,
                                      [statusId]: {
                                        ...(prev[statusId] || {}),
                                        [itemKey]: e.target.value ? Number(e.target.value) : null,
                                      },
                                    }))
                                  }
                                />
                              )}

                              {(req.config.itemType === 'text' || req.config.itemType === 'textarea') && (
                                <textarea
                                  className="mt-2 w-full rounded border border-gray-200 px-2 py-1 text-sm"
                                  value={typeof currentValue === 'string' ? currentValue : ''}
                                  onChange={(e) =>
                                    setRequirementValues((prev) => ({
                                      ...prev,
                                      [statusId]: {
                                        ...(prev[statusId] || {}),
                                        [itemKey]: e.target.value,
                                      },
                                    }))
                                  }
                                />
                              )}

                              {req.config.itemType === 'select' && (
                                <select
                                  className="mt-2 w-full rounded border border-gray-200 px-2 py-1 text-sm"
                                  value={typeof currentValue === 'string' ? currentValue : ''}
                                  onChange={(e) =>
                                    setRequirementValues((prev) => ({
                                      ...prev,
                                      [statusId]: {
                                        ...(prev[statusId] || {}),
                                        [itemKey]: e.target.value,
                                      },
                                    }))
                                  }
                                >
                                  <option value="">Selecciona una opcion</option>
                                  {(req.config.options || []).map((option) => (
                                    <option key={option} value={option}>
                                      {option}
                                    </option>
                                  ))}
                                </select>
                              )}

                              {isAttachment && (
                                <div className="mt-2 text-sm text-gray-600 space-y-1">
                                  <p>Archivos actuales: {String(countValue)}</p>
                                  {req.config.itemType === 'signature' ? (
                                    <p className="text-xs text-gray-500">
                                      La firma se registra desde el enlace enviado al cliente.
                                    </p>
                                  ) : (
                                    <p className="text-xs text-gray-500">
                                      Sube los archivos requeridos antes de continuar.
                                    </p>
                                  )}
                                </div>
                              )}

                              {req.config.itemType === 'photo' && source === 'booking_images' && (
                                <div className="mt-2">
                                  <input
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    capture="environment"
                                    className="block w-full text-xs"
                                    onChange={async (event) => {
                                      const files = event.target.files ? Array.from(event.target.files) : [];
                                      if (!files.length) return;
                                      try {
                                        const formData = new FormData();
                                        files.forEach((file) => formData.append('image', file));
                                        const response = await fetch(`${apiBase}/api/bookings/${booking.id}/images`, {
                                          method: 'POST',
                                          body: formData,
                                          credentials: 'include',
                                        });
                                        if (!response.ok) {
                                          const errorText = await response.text();
                                          let message = 'Error al subir imagen';
                                          try {
                                            const parsed = JSON.parse(errorText) as { error?: string };
                                            message = parsed.error || message;
                                          } catch {
                                            if (errorText) message = errorText;
                                          }
                                          throw new Error(message);
                                        }
                                        setAttachmentCounts((prev) => ({ ...prev, [itemKey]: countValue + files.length }));
                                        showSuccess('Fotos cargadas');
                                        refetchImages();
                                        refetchTransitions();
                                      } catch (err) {
                                        showError(err instanceof Error ? err.message : 'Error al subir imagen');
                                      } finally {
                                        event.target.value = '';
                                      }
                                    }}
                                  />
                                </div>
                              )}

                              {isAttachment && req.config.itemType !== 'signature' && source !== 'booking_images' && (
                                <div className="mt-2">
                                  <input
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    capture="environment"
                                    className="block w-full text-xs"
                                    onChange={async (event) => {
                                      const files = event.target.files ? Array.from(event.target.files) : [];
                                      if (!files.length) return;
                                      try {
                                        const formData = new FormData();
                                        files.forEach((file) => formData.append('file', file));
                                        const response = await fetch(
                                          `${apiBase}/api/bookings/${booking.id}/checklist/${req.config.id}/attachments`,
                                          {
                                            method: 'POST',
                                            body: formData,
                                            credentials: 'include',
                                          }
                                        );
                                        if (!response.ok) {
                                          const errorText = await response.text();
                                          let message = 'Error al subir archivo';
                                          try {
                                            const parsed = JSON.parse(errorText) as { error?: string };
                                            message = parsed.error || message;
                                          } catch {
                                            if (errorText) message = errorText;
                                          }
                                          throw new Error(message);
                                        }
                                        setAttachmentCounts((prev) => ({ ...prev, [itemKey]: countValue + files.length }));
                                        showSuccess('Archivos cargados');
                                        refetchTransitions();
                                      } catch (err) {
                                        showError(err instanceof Error ? err.message : 'Error al subir archivo');
                                      } finally {
                                        event.target.value = '';
                                      }
                                    }}
                                  />
                                </div>
                              )}

                              {!isComplete && req.errors && req.errors.length > 0 && (
                                <div className="mt-2 text-xs text-red-600">
                                  {req.errors.join(' · ')}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {group.customFields.length > 0 && (
                      <div className="space-y-3">
                        <p className="text-xs font-medium text-gray-600">Campos del flujo</p>
                        {group.customFields.map((field) => {
                          const value = dialogFieldValues[group.statusCode]?.[field.name];
                          if (field.type === 'textarea') {
                            return (
                              <div key={field.name} className="space-y-1">
                                <span className="text-sm font-medium text-gray-900">
                                  {field.label}{field.required && <span className="text-red-500 ml-1">*</span>}
                                </span>
                                <textarea
                                  className="w-full rounded border border-gray-200 px-2 py-1 text-sm"
                                  value={typeof value === 'string' ? value : ''}
                                  onChange={(e) =>
                                    setDialogFieldValues((prev) => ({
                                      ...prev,
                                      [group.statusCode]: {
                                        ...(prev[group.statusCode] || {}),
                                        [field.name]: e.target.value,
                                      },
                                    }))
                                  }
                                />
                              </div>
                            );
                          }

                          if (field.type === 'select') {
                            return (
                              <div key={field.name} className="space-y-1">
                                <span className="text-sm font-medium text-gray-900">
                                  {field.label}{field.required && <span className="text-red-500 ml-1">*</span>}
                                </span>
                                <select
                                  className="w-full rounded border border-gray-200 px-2 py-1 text-sm"
                                  value={typeof value === 'string' ? value : ''}
                                  onChange={(e) =>
                                    setDialogFieldValues((prev) => ({
                                      ...prev,
                                      [group.statusCode]: {
                                        ...(prev[group.statusCode] || {}),
                                        [field.name]: e.target.value,
                                      },
                                    }))
                                  }
                                >
                                  <option value="">Selecciona una opcion</option>
                                  {(field.options || []).map((option) => (
                                    <option key={option.value} value={option.value}>
                                      {option.label}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            );
                          }

                          if (field.type === 'checkbox') {
                            return (
                              <label key={field.name} className="flex items-center gap-2 text-sm">
                                <input
                                  type="checkbox"
                                  checked={value === true}
                                  onChange={(e) =>
                                    setDialogFieldValues((prev) => ({
                                      ...prev,
                                      [group.statusCode]: {
                                        ...(prev[group.statusCode] || {}),
                                        [field.name]: e.target.checked,
                                      },
                                    }))
                                  }
                                />
                                <span className="text-sm font-medium text-gray-900">
                                  {field.label}{field.required && <span className="text-red-500 ml-1">*</span>}
                                </span>
                              </label>
                            );
                          }

                          if (field.type === 'file') {
                            return (
                              <div key={field.name} className="space-y-1">
                                <span className="text-sm font-medium text-gray-900">
                                  {field.label}{field.required && <span className="text-red-500 ml-1">*</span>}
                                </span>
                                <input type="file" disabled className="block w-full text-xs" />
                              </div>
                            );
                          }

                          const inputType = field.type === 'number'
                            ? 'number'
                            : field.type === 'email'
                              ? 'email'
                              : field.type === 'phone'
                                ? 'tel'
                                : field.type === 'date'
                                  ? 'date'
                                  : 'text';

                          return (
                            <div key={field.name} className="space-y-1">
                              <span className="text-sm font-medium text-gray-900">
                                {field.label}{field.required && <span className="text-red-500 ml-1">*</span>}
                              </span>
                              <input
                                className="w-full rounded border border-gray-200 px-2 py-1 text-sm"
                                type={inputType}
                                value={typeof value === 'string' || typeof value === 'number' ? String(value) : ''}
                                onChange={(e) =>
                                  setDialogFieldValues((prev) => ({
                                    ...prev,
                                    [group.statusCode]: {
                                      ...(prev[group.statusCode] || {}),
                                      [field.name]: field.type === 'number'
                                        ? (e.target.value ? Number(e.target.value) : null)
                                        : e.target.value,
                                    },
                                  }))
                                }
                              />
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </DetailSection>
          )}


          <WorkflowChecklistSection
            bookingId={booking.id}
            jobId={booking.jobId}
            showAttachmentPreviews={false}
            onUpdated={() => {
              refetchTransitions();
              refetchImages();
              refetchWorkflowAttachments();
              refetch();
              refetchTimeline();
            }}
          />

          {/* Fotos (solo imágenes) */}
          <DetailSection
            title="Fotos"
            action={
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={handleSendUploadLink}
                  disabled={isSendingUploadLink}
                  className="inline-flex items-center gap-1 rounded border border-gray-200 px-2 py-1 text-[11px] text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  title="Enviar enlace de subida"
                >
                  {isSendingUploadLink ? <Loader2 className="h-3 w-3 animate-spin" /> : <Mail className="h-3 w-3" />}
                  Enviar enlace
                </button>
                <label className="p-0.5 hover:bg-gray-100 rounded cursor-pointer" title="Subir fotos">
                  <Plus className="h-3.5 w-3.5 text-gray-400" />
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/heic"
                    multiple
                    className="hidden"
                    onChange={async (event) => {
                      const files = event.target.files ? Array.from(event.target.files) : [];
                      if (!files.length) return;
                      try {
                        const optimizedFiles = await compressImages(files);
                        const formData = new FormData();
                        optimizedFiles.forEach((file) => formData.append('image', file));
                        const response = await fetch(`${apiBase}/api/bookings/${booking.id}/images`, {
                          method: 'POST',
                          body: formData,
                          credentials: 'include',
                        });
                        if (!response.ok) {
                          const errorText = await response.text();
                          let message = 'Error al subir imagen';
                          try {
                            const parsed = JSON.parse(errorText) as { error?: string };
                            message = parsed.error || message;
                          } catch { /* ignore */ }
                          throw new Error(message);
                        }
                        showSuccess(`${files.length} foto(s) subida(s)`);
                        refetchImages();
                      } catch (err) {
                        showError(err instanceof Error ? err.message : 'Error al subir imagen');
                      } finally {
                        event.target.value = '';
                      }
                    }}
                  />
                </label>
                <button type="button" onClick={() => refetchImages()} className="p-0.5 hover:bg-gray-100 rounded" title="Actualizar">
                  <ImageIcon className="h-3.5 w-3.5 text-gray-400" />
                </button>
              </div>
            }
            noPadding
          >
            <div className="p-3">
              {isLoadingImages ? (
                <div className="grid grid-cols-3 gap-2">
                  {[1, 2, 3].map((i) => <Skeleton key={i} className="aspect-square rounded" />)}
                </div>
              ) : bookingImagePhotos.length > 0 ? (
                <ImageThumbnailGrid
                  images={bookingImagePhotos.map((img) => ({
                    id: img.id,
                    url: `${apiBase}/api/bookings/${booking.id}/images/${img.id}`,
                    originalFilename: img.originalFilename,
                    uploadedAt: img.uploadedAt,
                  }))}
                  showDeleteButton={true}
                  onDelete={handleDeleteImage}
                  columns={3}
                  emptyMessage="Sin fotos"
                />
              ) : (
                <p className="text-xs text-gray-500 text-center py-2">Sin fotos</p>
              )}
            </div>
          </DetailSection>

          <DetailSection
            title="Documentos"
            action={
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={handleSendUploadLink}
                  disabled={isSendingUploadLink}
                  className="inline-flex items-center gap-1 rounded border border-gray-200 px-2 py-1 text-[11px] text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  title="Enviar enlace de subida"
                >
                  {isSendingUploadLink ? <Loader2 className="h-3 w-3 animate-spin" /> : <Mail className="h-3 w-3" />}
                  Enviar enlace
                </button>
                <label className="p-0.5 hover:bg-gray-100 rounded cursor-pointer" title="Subir documento">
                  <Plus className="h-3.5 w-3.5 text-gray-400" />
                  <input
                    type="file"
                    multiple
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.rtf,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,application/rtf"
                    className="hidden"
                    onChange={async (event) => {
                      const files = event.target.files ? Array.from(event.target.files) : [];
                      if (!files.length) return;
                      try {
                        const optimizedFiles = await compressImages(files);
                        const formData = new FormData();
                        optimizedFiles.forEach((file) => formData.append('image', file));
                        const response = await fetch(`${apiBase}/api/bookings/${booking.id}/images`, {
                          method: 'POST',
                          body: formData,
                          credentials: 'include',
                        });
                        if (!response.ok) {
                          const errorText = await response.text();
                          let message = 'Error al subir documento';
                          try {
                            const parsed = JSON.parse(errorText) as { error?: string };
                            message = parsed.error || message;
                          } catch { /* ignore */ }
                          throw new Error(message);
                        }
                        showSuccess(`${files.length} documento(s) subido(s)`);
                        refetchImages();
                      } catch (err) {
                        showError(err instanceof Error ? err.message : 'Error al subir documento');
                      } finally {
                        event.target.value = '';
                      }
                    }}
                  />
                </label>
                <label className="p-0.5 hover:bg-gray-100 rounded cursor-pointer" title="Escanear documento con cámara">
                  <Camera className="h-3.5 w-3.5 text-gray-400" />
                  <input
                    type="file"
                    multiple
                    accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                    capture="environment"
                    className="hidden"
                    onChange={async (event) => {
                      const files = event.target.files ? Array.from(event.target.files) : [];
                      if (!files.length) return;
                      try {
                        const optimizedFiles = await compressImages(files);
                        const formData = new FormData();
                        optimizedFiles.forEach((file) => formData.append('image', file));
                        const response = await fetch(`${apiBase}/api/bookings/${booking.id}/images`, {
                          method: 'POST',
                          body: formData,
                          credentials: 'include',
                        });
                        if (!response.ok) {
                          const errorText = await response.text();
                          let message = 'Error al escanear documento';
                          try {
                            const parsed = JSON.parse(errorText) as { error?: string };
                            message = parsed.error || message;
                          } catch { /* ignore */ }
                          throw new Error(message);
                        }
                        showSuccess(`${files.length} archivo(s) escaneado(s)`);
                        refetchImages();
                      } catch (err) {
                        showError(err instanceof Error ? err.message : 'Error al escanear documento');
                      } finally {
                        event.target.value = '';
                      }
                    }}
                  />
                </label>
                <button
                  type="button"
                  onClick={() => {
                    refetchImages();
                    refetchWorkflowAttachments();
                  }}
                  className="p-0.5 hover:bg-gray-100 rounded"
                  title="Actualizar"
                >
                  <FileText className="h-3.5 w-3.5 text-gray-400" />
                </button>
              </div>
            }
            noPadding
          >
            <div className="p-3">
              {isLoadingImages || isLoadingWorkflowAttachments ? (
                <div className="space-y-2">
                  {[1, 2, 3].map((i) => <Skeleton key={i} className="h-8 w-full rounded" />)}
                </div>
              ) : (bookingImageDocuments.length + processDocuments.length) > 0 ? (
                <div className="space-y-2">
                  {bookingImageDocuments.map((doc) => (
                    <a
                      key={doc.id}
                      href={`${apiBase}/api/bookings/${booking.id}/images/${doc.id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-2 rounded border border-gray-100 px-2 py-2 hover:bg-gray-50"
                      title={doc.originalFilename || 'Descargar'}
                    >
                      <FileText className="h-4 w-4 text-gray-500" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium text-gray-800 truncate">
                          {doc.originalFilename || 'Documento'}
                        </p>
                      </div>
                      <span className="text-[11px] text-blue-600">Abrir</span>
                    </a>
                  ))}

                  {processDocuments.map((att) => (
                    <a
                      key={att.id}
                      href={`${apiBase}${att.url}`}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-2 rounded border border-gray-100 px-2 py-2 hover:bg-gray-50"
                    >
                      <Paperclip className="h-4 w-4 text-gray-500" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium text-gray-800 truncate">{att.checklistItemLabel || 'Documento del proceso'}</p>
                        <p className="text-[11px] text-gray-500">{att.uploadedBy?.name || '—'}</p>
                      </div>
                      <span className="text-[11px] text-blue-600">Abrir</span>
                    </a>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-500 text-center py-2">Sin documentos</p>
              )}
            </div>
          </DetailSection>

          <DetailSection
            title="Firmas"
            action={
              <button type="button" onClick={() => { refetchWorkflowAttachments(); refetchImages(); }} className="p-0.5 hover:bg-gray-100 rounded" title="Actualizar">
                <PenTool className="h-3.5 w-3.5 text-gray-400" />
              </button>
            }
            noPadding
          >
            <div className="p-3 space-y-3">
              {/* Signature template picker */}
              <div className="space-y-2">
                <select
                  className="w-full rounded border border-gray-200 px-2 py-1.5 text-sm"
                  value={selectedSignatureTemplateId}
                  onChange={(e) => setSelectedSignatureTemplateId(e.target.value)}
                  disabled={isSendingSignatureLink}
                >
                  <option value="">Plantilla por defecto del sistema</option>
                  {signatureTemplates.map((tpl) => (
                    <option key={tpl.id} value={tpl.id}>
                      {tpl.name}{tpl.isDefault ? ' (defecto)' : ''}
                    </option>
                  ))}
                  <option value="__custom__">✏️ Mensaje personalizado...</option>
                </select>
                <button
                  type="button"
                  onClick={() => {
                    if (selectedSignatureTemplateId === '__custom__') {
                      const message = prompt('Mensaje personalizado para el cliente:');
                      if (message !== null) void handleSendSignatureLink({ message });
                    } else if (selectedSignatureTemplateId) {
                      void handleSendSignatureLink({ templateId: selectedSignatureTemplateId });
                    } else {
                      void handleSendSignatureLink();
                    }
                  }}
                  disabled={isSendingSignatureLink}
                  className="w-full inline-flex items-center justify-center gap-1.5 rounded border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  {isSendingSignatureLink ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />}
                  Enviar enlace de firma
                </button>
              </div>

              {/* Existing signatures with details */}
              {isLoadingWorkflowAttachments || isLoadingImages || isTimelineLoading ? (
                <div className="space-y-2">
                  {[1, 2].map((i) => <Skeleton key={i} className="h-28 w-full rounded" />)}
                </div>
              ) : (signatureEntries.length > 0 || signatureDeclines.length > 0) ? (
                <div className="space-y-3">
                  {signatureDeclines.map((decline) => (
                    <div key={decline.id} className="rounded-lg border border-orange-200 bg-orange-50 px-3 py-2">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-xs font-medium text-orange-900">{decline.templateName}</p>
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-orange-200 text-orange-900">Rechazada</span>
                      </div>
                      {decline.message && (
                        <p className="text-[11px] text-orange-800 mt-0.5 italic">&ldquo;{decline.message}&rdquo;</p>
                      )}
                      <p className="text-[11px] text-orange-700 mt-0.5">
                        {new Date(decline.createdAt * 1000).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  ))}
                  {signatureEntries.map((sig) => (
                    <div key={sig.id} className="rounded-lg border border-gray-200 overflow-hidden bg-white">
                      <a href={sig.url} target="_blank" rel="noreferrer" className="block">
                        <img src={sig.url} alt={sig.label} className="h-28 w-full object-contain bg-gray-50" />
                      </a>
                      <div className="px-3 py-2 border-t border-gray-100">
                        <p className="text-xs font-medium text-gray-800">{sig.label}</p>
                        {sig.message && (
                          <p className="text-[11px] text-gray-500 mt-0.5 line-clamp-2 italic">&ldquo;{sig.message}&rdquo;</p>
                        )}
                        <div className="flex items-center gap-2 mt-0.5">
                          {'uploadedBy' in sig && sig.uploadedBy && <span className="text-[11px] text-gray-500">{sig.uploadedBy}</span>}
                          {sig.signedAt && (
                            <span className="text-[11px] text-gray-400">{sig.signedAt.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-500 text-center">Sin firmas</p>
              )}
            </div>
          </DetailSection>

          <div>
            <div className="flex items-center gap-2 mb-2">
              <History className="h-4 w-4 text-gray-400" />
              <span className="text-xs font-medium text-gray-700">Actividad</span>
            </div>
            <JobTimeline events={bookingTimeline} loading={isTimelineLoading} />
          </div>
        </div>

        {/* Sidebar */}
        <div className={detailStyles.sidebar}>
          {/* Actions - using workflow-based transitions */}
          <BookingActions
            bookingId={booking.id}
            jobId={booking.jobId}
            bookingReference={booking.bookingReference}
            customerName={booking.customer?.name}
            customerEmail={booking.customer?.email}
            bookingType={booking.bookingType}
            currentStatus={booking.status || 'UNVERIFIED'}
            prefillCustomFields={dialogFieldValues}
            isEmailVerified={booking.confirmed}
            tokenExpires={booking.createdAt ? new Date((booking.createdAt + 24 * 60 * 60) * 1000) : undefined}
            onResendEmail={handleResendConfirmation}
            isResendingEmail={resendConfirmationMutation.isPending}
            onResendNotification={() => {
              if (!booking) return;
              resendNotificationMutation.mutate(booking.id, {
                onSuccess: () => showSuccess('Notificación reenviada'),
                onError: () => showError('Error al reenviar notificación'),
              });
            }}
            isResendingNotification={resendNotificationMutation.isPending}
            onReschedule={() => setRescheduleModalOpen(true)}
            onCancel={() => setCancelModalOpen(true)}
            onSuccess={() => {
              showSuccess('Estado actualizado');
              refetch();
            }}
            onUploadComplete={() => {
              refetchImages();
              refetchTransitions();
            }}
            showCustomerLinkActions={false}
            onError={(err) => showError(err)}
          />

          {/* Tracking */}
          <DetailSection title="Trabajo">
            <div className="p-3 space-y-1">
              {booking.jobId ? (
                <Link to={`/jobs?jobId=${booking.jobId}`} className="text-sm text-blue-600 hover:underline">
                  Ver trabajo vinculado
                </Link>
              ) : (
                <p className="text-xs text-gray-500">Sin trabajo vinculado</p>
              )}
            </div>
          </DetailSection>

          <DetailSection title="Seguimiento">
            <div className="p-3">
              <TrackingLinkGenerator
                bookingId={booking.id}
                bookingReference={booking.bookingReference}
                vehiclePlate={booking.vehicle?.registration}
                trackingToken={booking.trackingToken}
                onSuccess={showSuccess}
                onError={showError}
              />
            </div>
          </DetailSection>
        </div>
      </div>

      <CancellationModal
        open={cancelModalOpen}
        onOpenChange={setCancelModalOpen}
        bookingId={booking.id}
        bookingReference={booking.bookingReference}
        customerName={booking.customer?.name}
        scheduledAt={booking.scheduledAt}
        onSuccess={handleCancelSuccess}
        invalidateKeys={[['booking', booking.id]]}
      />

      <RescheduleFlow
        open={rescheduleModalOpen}
        onOpenChange={setRescheduleModalOpen}
        bookingId={booking.id}
        currentScheduledAt={booking.scheduledAt}
        bookingReference={booking.bookingReference}
        customerName={booking.customer?.name}
        onSuccess={handleRescheduleSuccess}
        invalidateKeys={[['booking', booking.id]]}
      />
    </div>
  );
}
